import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import cn from 'classnames';
import { orderBy, compact } from 'lodash-es';
import { Page, TableList, PageEmptyState, PageErrorState, Searchbox, FilterPickerGroup, Button, PageContentHeader, Typography, MultiSelectFilterPicker, ConfirmationModal } from 'seagull';
import { Plus } from 'seagull/icons';
import { useStateFromUrl } from 'seagull/utils/useStateFromUrl';
import AccountantAccessLearnMoreButton from '~/components/AccountantAccessLearnMoreButton';
import { ManageContactModalForVendor } from '~/components/ManageContactModal';
import { routes } from '~/constants';
import { useEventReporter, useOverlayContext, useStore, useStoreActions } from '~/contexts';
import { useContacts } from '~/hooks/reactQuery/queries/useContacts';
import { useContactActions } from '~/hooks/useContactActions';
import { useDebouncedLogSearchToBI } from '~/hooks/useDebouncedLogSearchToBI';
import { useEnterAccountantAccess } from '~/hooks/useEnterAccountantAccess';
import { useFeatureFlags } from '~/hooks/useFeatureFlags';
import { useInfiniteScroll } from '~/hooks/useInfiniteScroll';
import { usePendingContactEmailUpdates } from '~/hooks/usePendingContactEmailUpdates';
import { useSetDocumentTitle } from '~/hooks/useSetDocumentTitle';
import { useSortFromUrl } from '~/hooks/useSortFromUrl';
import { useLocaleKeys } from '~/i18n';
import { ErrorResponse } from '~/infra/ErrorResponse';
import { handleRequest } from '~/infra/utils';
import { ContactModel, ContactStatus } from '~/models/ContactModel';
import { BusinessChannelEventBinder, PusherEvent } from '~/pusher';
import { selectors } from '~/store/state-selectors';
import { Accounting } from '~/types/api.model';
import { CreateProposalButton } from '../../components/CreateProposalButton/CreateProposalButton';
import PageLoadingState from '../../components/PageLoadingState';
import ContactsBulkActionResultModal from './ContactsBulkActionResultModal';
import ContactsBulkActions from './ContactsBulkActions';
import styles from './ContactsDashboard.module.scss';
import { contactBiData, contactStateKey } from './ContactsDashboard.utils';
import ContactsDashboardRow from './ContactsDashboardRow';
import { EmptyStateIllustration } from './EmptyStateIllustration';
import { useRowSelection } from './hooks/useRowSelection';
import VendorAccountantAccessModal from './VendorAccountantAccessModal';
const sortIds = [
    'companyName',
    'status',
    'primaryContactName',
    'contactDetails'
];
const columnDefs = [
    {
        width: [
            140,
            300
        ],
        sortable: true
    },
    {
        width: [
            140,
            300
        ],
        sortable: true
    },
    {
        width: [
            170,
            170
        ],
        sortable: true
    },
    {
        width: [
            160,
            300
        ],
        sortable: true
    }
];
const singleContactBiEventIds = {
    archive: {
        rowMenuClick: 11617,
        confirm: 11619,
        back: 11621,
        close: 11622,
        success: 11625
    },
    delete: {
        rowMenuClick: 11618,
        confirm: 11620,
        back: 11623,
        close: 11624,
        success: 11626
    }
};
export const importContactsRoute = {
    id: 'import-contacts',
    text: 'Import contacts',
    url: routes.importContacts,
    shouldShow: (param)=>{
        let { isVendor } = param;
        return isVendor;
    }
};
export function ActualContactsDashboard(props) {
    const { data: contacts, isRefreshing, refresh, optimisticUpdate } = props;
    const qboCustomers = useStore((state)=>state.qboCustomers.data);
    const xeroCustomers = useStore((state)=>state.xeroCustomers.data);
    const { api, fetchAccountingCustomers, logBI, fetchIntegrationsSummary, fetchUserInfo } = useStoreActions();
    const { reportToGtm, reportToAppcues } = useEventReporter();
    const { startLoader, stopLoader, showErrorToast, showSuccessToast, showModal } = useOverlayContext();
    const defaultSort = useMemo(()=>({
            colIndex: 0,
            sort: 'desc'
        }), []);
    const { sort: tableSort, changeSort } = useSortFromUrl({
        defaultSort,
        sortIds
    });
    const scrollElRef = useRef(null);
    const debouncedLogSearchToBI = useDebouncedLogSearchToBI(11602);
    const { urlState: { search, status: contactStatusFilterValue }, changeUrlState } = useStateFromUrl({
        search: {
            defaultValue: ''
        },
        status: {
            defaultValue: [
                ContactStatus.Active
            ]
        }
    });
    const hasContacts = !!(contacts && contacts.length);
    const showQboIntegration = useStore(selectors.integration.showQboIntegration);
    const showXeroIntegration = useStore(selectors.integration.showXeroIntegration);
    const importContactsLink = useStore(selectors.filterAvailableRoutes([
        importContactsRoute
    ]))[0];
    const enterAccountantAccess = useEnterAccountantAccess();
    const { withdrawToEditContactProposals } = useContactActions();
    const [registerSearchBoxHotkey, setRegisterSearchBoxHotkey] = useState(true);
    const { ALLOW_EDIT_CONTACT, BULK_DELETE_CONTACTS } = useFeatureFlags();
    const { t: contactsDashboardTexts } = useLocaleKeys('pages.contactsDashboard');
    const { t: deleteConfirmationTexts } = useLocaleKeys('pages.contactsDashboard.deleteConfirmation');
    const { t: archiveConfirmationTexts } = useLocaleKeys('pages.contactsDashboard.archiveConfirmation');
    const { t: bulkDeleteConfirmationTexts } = useLocaleKeys('pages.contactsDashboard.bulkDeleteConfirmation');
    const { t: columnHeaderTexts } = useLocaleKeys('pages.contactsDashboard.columnHeaders');
    const { t: statusFilterTexts } = useLocaleKeys('pages.contactsDashboard.contactStatusFilter');
    const { t: emptyStateTexts } = useLocaleKeys('pages.contactsDashboard.emptyState');
    const { t: legacyArchiveTexts } = useLocaleKeys('pages.contactsDashboard.legacyArchive');
    const { t: accountantAccessTexts } = useLocaleKeys('pages.contactsDashboard.accountantAccess');
    const headerItems = useMemo(()=>[
            {
                id: 'companyName',
                value: columnHeaderTexts.companyName()
            },
            {
                id: 'status',
                value: columnHeaderTexts.status()
            },
            {
                id: 'primaryContactName',
                value: columnHeaderTexts.contactName()
            },
            {
                id: 'contactDetails',
                value: columnHeaderTexts.contactDetails()
            }
        ], [
        columnHeaderTexts
    ]);
    const contactStatusFilterOptions = useMemo(()=>[
            {
                value: ContactStatus.Active,
                label: statusFilterTexts.active()
            },
            {
                value: ContactStatus.Archived,
                label: statusFilterTexts.archived()
            }
        ], [
        statusFilterTexts
    ]);
    const { isPending, markPending, markComplete } = usePendingContactEmailUpdates();
    const onContactUpdated = useCallback((event)=>{
        markComplete(event.payload.contactUpdated.userId);
        refresh();
    }, [
        markComplete,
        refresh
    ]);
    useEffect(()=>{
        const promises = compact([
            showQboIntegration && fetchAccountingCustomers(Accounting.QBO),
            showXeroIntegration && fetchAccountingCustomers(Accounting.Xero),
            (showQboIntegration || showXeroIntegration) && fetchIntegrationsSummary()
        ]);
        if (promises.length > 0) {
            Promise.all(promises);
        }
    }, [
        showQboIntegration,
        showXeroIntegration,
        fetchAccountingCustomers,
        fetchIntegrationsSummary
    ]);
    const qboCustomerIdToDisplay = useMemo(()=>{
        if (!qboCustomers) return {};
        return qboCustomers.reduce((prev, qboCustomer)=>{
            prev[qboCustomer.id()] = qboCustomer.formattedDisplay();
            return prev;
        }, {});
    }, [
        qboCustomers
    ]);
    const xeroCustomerIdToDisplay = useMemo(()=>{
        if (!xeroCustomers) return {};
        return xeroCustomers.reduce((prev, xeroCustomer)=>{
            prev[xeroCustomer.id()] = xeroCustomer.formattedDisplay();
            return prev;
        }, {});
    }, [
        xeroCustomers
    ]);
    const filteredAndSorted = useMemo(()=>{
        const filtered = (contacts || []).filter((contact)=>{
            // search
            const trimmedSearch = search ? search.trim().toLowerCase() : '';
            const termForSearch = "".concat(contact.companyName(), " ").concat(contact.firstName(), " ").concat(contact.lastName(), " ").concat(contact.email());
            const passesSearch = termForSearch.toLowerCase().includes(trimmedSearch);
            // status filter
            const passesStatusFilter = contactStatusFilterValue.includes(contact.status());
            return passesSearch && passesStatusFilter;
        });
        const { sort, colIndex } = tableSort || {
            colIndex: 0,
            sort: 'asc'
        };
        const sortPropertiesByHeaderIndex = [
            [
                (c)=>c.companyName()
            ],
            [
                (s)=>s.status(),
                (c)=>c.companyName()
            ],
            [
                (c)=>c.firstName(),
                (c)=>c.lastName(),
                (c)=>c.companyName()
            ],
            [
                (c)=>c.email(),
                (c)=>c.companyName()
            ]
        ];
        const sortProperties = sortPropertiesByHeaderIndex[colIndex];
        const sortingOrder = sort === 'asc' ? 'desc' : 'asc';
        const orders = Array.from(Array(sortProperties.length)).map(()=>sortingOrder);
        return orderBy(filtered, sortProperties, orders);
    }, [
        contacts,
        tableSort,
        search,
        contactStatusFilterValue
    ]);
    const { selected, numSelected, numVisibleSelected, setSelected, onToggleAllSelection, getSelectedIds, clearSelection } = useRowSelection({
        items: filteredAndSorted
    });
    const { paginatedData, onScroll } = useInfiniteScroll({
        data: filteredAndSorted,
        scrollElRef
    });
    const onSortChange = (sort)=>{
        logBI(11603, {
            column: sort ? headerItems[sort.colIndex].id : 'none'
        });
        changeSort(sort);
    };
    const onClearSearch = ()=>{
        changeUrlState({
            search: ''
        });
        debouncedLogSearchToBI.onClear();
    };
    function onClearFilters() {
        changeUrlState({
            search: '',
            status: contactStatusFilterOptions.map((opt)=>opt.value)
        });
    }
    async function handleOptimisticUpdate(param) {
        let { contact, savedContactData } = param;
        if (optimisticUpdate && contact && ALLOW_EDIT_CONTACT) {
            optimisticUpdate((currentContacts)=>currentContacts.map((c)=>c.id() === savedContactData.id ? new ContactModel(_object_spread_props(_object_spread({}, c.toJSON(), savedContactData.contactData), {
                        version: savedContactData.version
                    })) : c));
        } else {
            await refresh();
        }
    }
    const openContactModal = async (contact)=>{
        logBI(11600, {
            contact: contact && contact.toJSON()
        });
        reportToGtm('button-click', 'contacts-table-view-contact');
        reportToAppcues('button-click', 'contacts-table-view-contact');
        setRegisterSearchBoxHotkey(false);
        try {
            const savedContactData = await showModal({
                closeOnEscapeKey: true,
                closeOnClickOutside: true,
                content: (param)=>{
                    let { dismissModal } = param;
                    return /*#__PURE__*/ _jsx(ManageContactModalForVendor, {
                        testId: "manage-contact-modal",
                        existingContact: contact,
                        onClose: dismissModal,
                        origin: "Contacts dashboard",
                        markPending: markPending,
                        markComplete: markComplete
                    });
                }
            });
            if (savedContactData) {
                handleOptimisticUpdate({
                    contact,
                    savedContactData
                });
            }
        } finally{
            setRegisterSearchBoxHotkey(true);
        }
    };
    const onRestoreContact = async (contact)=>{
        logBI(11605, {
            contact: {
                firstName: contact.firstName(),
                lastName: contact.lastName(),
                email: contact.email()
            }
        });
        const loader = 'restore-contact-loader';
        startLoader(loader);
        const result = await handleRequest(api.contact.restore(contact.id(), {
            version: contact.version()
        }));
        if (result.success) {
            await refresh();
        } else {
            showErrorToast(result.error.restoreContactErrorText(), 'restore-error-toast');
        }
        stopLoader(loader);
    };
    const onArchiveContact = async (contact)=>{
        try {
            if (BULK_DELETE_CONTACTS) {
                logBI(singleContactBiEventIds.archive.rowMenuClick, contactBiData(contact));
                const stateKey = contactStateKey(contact);
                const confirmed = await showModal({
                    content: (param)=>{
                        let { dismissModal } = param;
                        return /*#__PURE__*/ _jsx(ConfirmationModal, {
                            skin: "danger",
                            textAlignCenter: false,
                            title: archiveConfirmationTexts.title[stateKey](),
                            content: /*#__PURE__*/ _jsx(Typography, {
                                showWhiteSpaces: true,
                                children: archiveConfirmationTexts.content[stateKey]({
                                    fullName: contact.fullName()
                                })
                            }),
                            primaryButtonLabel: archiveConfirmationTexts.primaryButton(),
                            secondaryButtonLabel: archiveConfirmationTexts.secondaryButton(),
                            onPrimaryButtonClick: ()=>dismissModal(true),
                            onSecondaryButtonClick: ()=>{
                                logBI(singleContactBiEventIds.archive.back, contactBiData(contact));
                                dismissModal(false);
                            },
                            onCloseButtonClick: ()=>{
                                logBI(singleContactBiEventIds.archive.close, contactBiData(contact));
                                dismissModal(false);
                            }
                        });
                    }
                });
                if (confirmed) {
                    logBI(singleContactBiEventIds.archive.confirm, contactBiData(contact));
                    logBI(11604, {
                        contact: {
                            firstName: contact.firstName(),
                            lastName: contact.lastName(),
                            email: contact.email()
                        }
                    });
                    startLoader('archive-contact-loader');
                    await api.contact.archive(contact.id(), {
                        version: contact.version()
                    });
                    logBI(singleContactBiEventIds.archive.success, contactBiData(contact));
                    showSuccessToast(contactsDashboardTexts.archiveSuccessToast(), 'archive-success-toast');
                    await refresh();
                }
            } else {
                const shouldWithdrawProposals = contact.hasPendingProposals();
                const confirmed = await showModal({
                    content: (param)=>{
                        let { dismissModal } = param;
                        return /*#__PURE__*/ _jsx(ConfirmationModal, {
                            skin: "danger",
                            title: shouldWithdrawProposals ? legacyArchiveTexts.proposalsAwaitingTitle() : legacyArchiveTexts.archiveClientTitle(),
                            content: shouldWithdrawProposals ? legacyArchiveTexts.archiveWithWithdraw({
                                fullName: contact.fullName()
                            }) : legacyArchiveTexts.archiveConfirm({
                                fullName: contact.fullName()
                            }),
                            primaryButtonLabel: shouldWithdrawProposals ? legacyArchiveTexts.archiveAndWithdrawButton() : legacyArchiveTexts.archiveButton(),
                            secondaryButtonLabel: legacyArchiveTexts.cancel(),
                            onPrimaryButtonClick: ()=>dismissModal(true),
                            onSecondaryButtonClick: ()=>dismissModal(false),
                            primaryButtonTestId: shouldWithdrawProposals ? 'confirm-withdraw-and-archive-btn' : undefined
                        });
                    }
                });
                if (confirmed) {
                    logBI(11604, {
                        contact: {
                            firstName: contact.firstName(),
                            lastName: contact.lastName(),
                            email: contact.email()
                        }
                    });
                    startLoader('archive-contact-loader');
                    if (shouldWithdrawProposals) {
                        await withdrawToEditContactProposals(contact.id());
                    }
                    await api.contact.archive(contact.id(), {
                        version: contact.version()
                    });
                    showSuccessToast(contactsDashboardTexts.archiveSuccessToast(), 'archive-success-toast');
                    await refresh();
                }
            }
        } catch (err) {
            showErrorToast(new ErrorResponse(err).archiveContactErrorText(), 'archive-error-toast');
        } finally{
            stopLoader('archive-contact-loader');
        }
    };
    const onDeleteContact = async (contact)=>{
        try {
            logBI(singleContactBiEventIds.delete.rowMenuClick, contactBiData(contact));
            const stateKey = contactStateKey(contact);
            const confirmed = await showModal({
                content: (param)=>{
                    let { dismissModal } = param;
                    return /*#__PURE__*/ _jsx(ConfirmationModal, {
                        skin: "danger",
                        textAlignCenter: false,
                        title: deleteConfirmationTexts.title[stateKey](),
                        content: /*#__PURE__*/ _jsx(Typography, {
                            showWhiteSpaces: true,
                            children: deleteConfirmationTexts.content[stateKey]({
                                fullName: contact.fullName()
                            })
                        }),
                        primaryButtonLabel: deleteConfirmationTexts.primaryButton(),
                        secondaryButtonLabel: deleteConfirmationTexts.secondaryButton(),
                        onPrimaryButtonClick: ()=>dismissModal(true),
                        onSecondaryButtonClick: ()=>{
                            logBI(singleContactBiEventIds.delete.back, contactBiData(contact));
                            dismissModal(false);
                        },
                        onCloseButtonClick: ()=>{
                            logBI(singleContactBiEventIds.delete.close, contactBiData(contact));
                            dismissModal(false);
                        }
                    });
                }
            });
            if (confirmed) {
                logBI(singleContactBiEventIds.delete.confirm, contactBiData(contact));
                startLoader('delete-contact-loader');
                await api.contact.delete(contact.id());
                logBI(singleContactBiEventIds.delete.success, contactBiData(contact));
                showSuccessToast(contactsDashboardTexts.deleteSuccessToast(), 'delete-success-toast');
                await refresh();
            }
        } catch (err) {
            showErrorToast(new ErrorResponse(err).deleteContactErrorText(), 'delete-error-toast');
        } finally{
            stopLoader('delete-contact-loader');
        }
    };
    const onBulkActionComplete = useCallback(()=>{
        clearSelection();
        refresh();
    }, [
        clearSelection,
        refresh
    ]);
    const onBulkArchive = async ()=>{
        const ids = getSelectedIds();
        if (ids.length === 0) return;
        await showModal({
            content: (param)=>{
                let { dismissModal } = param;
                return /*#__PURE__*/ _jsx(ContactsBulkActionResultModal, {
                    actionType: "archive",
                    contactIds: ids,
                    dismissModal: dismissModal,
                    onComplete: onBulkActionComplete
                });
            }
        });
    };
    const onBulkDelete = async ()=>{
        const ids = getSelectedIds();
        if (ids.length === 0) return;
        const confirmed = await showModal({
            content: (param)=>{
                let { dismissModal } = param;
                return /*#__PURE__*/ _jsx(ConfirmationModal, {
                    skin: "danger",
                    textAlignCenter: false,
                    title: bulkDeleteConfirmationTexts.title(),
                    content: /*#__PURE__*/ _jsx(Typography, {
                        showWhiteSpaces: true,
                        children: bulkDeleteConfirmationTexts.content({
                            count: ids.length
                        })
                    }),
                    primaryButtonLabel: bulkDeleteConfirmationTexts.primaryButton(),
                    secondaryButtonLabel: bulkDeleteConfirmationTexts.secondaryButton(),
                    onPrimaryButtonClick: ()=>dismissModal(true),
                    onSecondaryButtonClick: ()=>dismissModal(false),
                    onCloseButtonClick: ()=>dismissModal(false)
                });
            }
        });
        if (!confirmed) return;
        await showModal({
            content: (param)=>{
                let { dismissModal } = param;
                return /*#__PURE__*/ _jsx(ContactsBulkActionResultModal, {
                    actionType: "delete",
                    contactIds: ids,
                    dismissModal: dismissModal,
                    onComplete: onBulkActionComplete
                });
            }
        });
    };
    function onRequestAccountantAccess(contact) {
        showModal({
            closeOnClickOutside: true,
            closeOnEscapeKey: true,
            content: (param)=>{
                let { dismissModal } = param;
                return /*#__PURE__*/ _jsx(VendorAccountantAccessModal, {
                    dismissModal: dismissModal,
                    primaryButtonLabel: accountantAccessTexts.requestAccess(),
                    title: accountantAccessTexts.requestTitle(),
                    primaryButtonTestId: "request-acc-access-submit",
                    content: /*#__PURE__*/ _jsxs("div", {
                        children: [
                            /*#__PURE__*/ _jsx(Typography, {
                                className: styles.requestAccAccessParagraph,
                                children: accountantAccessTexts.requestDescription()
                            }),
                            /*#__PURE__*/ _jsx("ul", {
                                className: cn(styles.requestAccAccessParagraph, styles.requestAccAccessList),
                                children: [
                                    accountantAccessTexts.requestNotice1(),
                                    accountantAccessTexts.requestNotice2(),
                                    accountantAccessTexts.requestNotice3()
                                ].map((text, index)=>/*#__PURE__*/ _jsx("li", {
                                        children: /*#__PURE__*/ _jsx(Typography, {
                                            children: text
                                        })
                                    }, index))
                            }),
                            /*#__PURE__*/ _jsx(AccountantAccessLearnMoreButton, {
                                isVendor: true
                            })
                        ]
                    }),
                    failureToastProps: {
                        text: accountantAccessTexts.requestFailure(),
                        testId: 'acc-access-request-failure'
                    },
                    onSuccess: refresh,
                    onSubmit: async ()=>{
                        const customerId = contact.customerId();
                        const companyName = contact.companyName();
                        if (!customerId) return;
                        logBI(13100, {
                            customerId,
                            companyName
                        });
                        await api.accountants.requestAccess(customerId);
                    }
                });
            }
        });
    }
    function onCancelAccountantAccessRequest(contact) {
        showModal({
            closeOnClickOutside: true,
            closeOnEscapeKey: true,
            content: (param)=>{
                let { dismissModal } = param;
                return /*#__PURE__*/ _jsx(VendorAccountantAccessModal, {
                    dismissModal: dismissModal,
                    skin: "danger",
                    primaryButtonLabel: accountantAccessTexts.cancelRequest(),
                    title: accountantAccessTexts.cancelTitle(),
                    primaryButtonTestId: "cancel-acc-access-request-submit",
                    content: /*#__PURE__*/ _jsx(Typography, {
                        children: accountantAccessTexts.cancelConfirm({
                            companyName: contact.companyName()
                        })
                    }),
                    failureToastProps: {
                        text: accountantAccessTexts.cancelFailure(),
                        testId: 'cancel-acc-access-request-failure'
                    },
                    onSuccess: refresh,
                    onSubmit: async ()=>{
                        const customerId = contact.customerId();
                        const companyName = contact.companyName();
                        if (!customerId) return;
                        logBI(13103, {
                            customerId,
                            companyName
                        });
                        await api.accountants.cancelRequestAccess(customerId);
                    }
                });
            }
        });
    }
    function onRemoveAccountantAccess(contact) {
        showModal({
            closeOnClickOutside: true,
            closeOnEscapeKey: true,
            content: (param)=>{
                let { dismissModal } = param;
                return /*#__PURE__*/ _jsx(VendorAccountantAccessModal, {
                    dismissModal: dismissModal,
                    skin: "danger",
                    primaryButtonLabel: accountantAccessTexts.removeAccess(),
                    title: accountantAccessTexts.removeTitle(),
                    primaryButtonTestId: "remove-acc-access-submit",
                    content: /*#__PURE__*/ _jsxs("div", {
                        children: [
                            /*#__PURE__*/ _jsx(Typography, {
                                children: accountantAccessTexts.removeConfirm({
                                    companyName: contact.companyName()
                                })
                            }),
                            /*#__PURE__*/ _jsx(Typography, {
                                children: accountantAccessTexts.removeNotice()
                            })
                        ]
                    }),
                    failureToastProps: {
                        text: accountantAccessTexts.removeFailure(),
                        testId: 'remove-acc-access-failure'
                    },
                    // We fetchUserInfo to remove the accountant workspace from userInfo.workspaces
                    onSuccess: ()=>Promise.all([
                            refresh(),
                            fetchUserInfo()
                        ]),
                    onSubmit: async ()=>{
                        const customerId = contact.customerId();
                        const companyName = contact.companyName();
                        if (!customerId) return;
                        logBI(13104, {
                            customerId,
                            companyName
                        });
                        await api.accountants.removeMe(customerId);
                    }
                });
            }
        });
    }
    return /*#__PURE__*/ _jsxs(Page, {
        testId: "contacts-dashboard",
        ref: scrollElRef,
        header: contactsDashboardTexts.title(),
        subheader: contactsDashboardTexts.subheader(),
        actions: /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                importContactsLink && /*#__PURE__*/ _jsx(Button, {
                    skin: "primaryOutline",
                    as: Link,
                    to: importContactsLink.url,
                    testId: "goto-".concat(importContactsLink.id),
                    children: contactsDashboardTexts.importContacts()
                }),
                /*#__PURE__*/ _jsx(Button, {
                    testId: "create-contact-btn",
                    prefixIcon: /*#__PURE__*/ _jsx(Plus, {
                        size: 18
                    }),
                    onClick: ()=>openContactModal(),
                    children: contactsDashboardTexts.createContact()
                })
            ]
        }),
        loading: isRefreshing,
        onScroll: onScroll,
        children: [
            ALLOW_EDIT_CONTACT && /*#__PURE__*/ _jsx(BusinessChannelEventBinder, {
                source: "mgmt",
                eventName: PusherEvent.ContactUpdated,
                callback: onContactUpdated
            }),
            /*#__PURE__*/ _jsx(TableList.Provider, _object_spread_props(_object_spread({
                hasActionColumn: true,
                columnDefs: columnDefs,
                sort: tableSort,
                onSortChange: onSortChange
            }, BULK_DELETE_CONTACTS && {
                enableRowSelection: true,
                selectedRows: selected,
                onSelectionChange: setSelected,
                numRows: filteredAndSorted.length,
                numSelectedRows: numVisibleSelected,
                onToggleAllSelection
            }), {
                children: hasContacts ? /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        /*#__PURE__*/ _jsxs(PageContentHeader, {
                            children: [
                                /*#__PURE__*/ _jsxs(FilterPickerGroup, {
                                    style: {
                                        marginBlockEnd: '24px'
                                    },
                                    children: [
                                        /*#__PURE__*/ _jsx(Searchbox, {
                                            registerHotkey: registerSearchBoxHotkey,
                                            value: search,
                                            testId: "contacts-dashboard-search",
                                            onChange: (event)=>{
                                                const searchStr = event.target.value;
                                                changeUrlState({
                                                    search: searchStr
                                                });
                                                debouncedLogSearchToBI.onChange(searchStr);
                                            },
                                            onClear: onClearSearch
                                        }),
                                        /*#__PURE__*/ _jsx(MultiSelectFilterPicker, {
                                            testId: "contact-status-filter",
                                            value: contactStatusFilterValue,
                                            options: contactStatusFilterOptions,
                                            label: statusFilterTexts.label(),
                                            onChange: (newFilterValue)=>{
                                                changeUrlState({
                                                    status: newFilterValue.length ? newFilterValue : contactStatusFilterOptions.map((opt)=>opt.value)
                                                });
                                            }
                                        }),
                                        BULK_DELETE_CONTACTS && /*#__PURE__*/ _jsx(ContactsBulkActions, {
                                            numSelected: numSelected,
                                            onBulkArchive: onBulkArchive,
                                            onBulkDelete: onBulkDelete
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsx(TableList.Header, {
                                    items: headerItems,
                                    testId: "contacts-dashboard-table-header"
                                })
                            ]
                        }),
                        filteredAndSorted.length ? paginatedData.map((contact)=>/*#__PURE__*/ _jsx(ContactsDashboardRow, {
                                contact: contact,
                                isQboIntegrated: showQboIntegration,
                                isXeroIntegrated: showXeroIntegration,
                                qboCustomerIdToDisplay: qboCustomerIdToDisplay,
                                xeroCustomerIdToDisplay: xeroCustomerIdToDisplay,
                                openContactModal: openContactModal,
                                onRequestAccountantAccess: onRequestAccountantAccess,
                                onCancelAccountantAccessRequest: onCancelAccountantAccessRequest,
                                onRemoveAccountantAccess: onRemoveAccountantAccess,
                                onGotoClientPortal: (c)=>enterAccountantAccess(c.customerId(), c.companyName()),
                                onRestoreContact: onRestoreContact,
                                onArchiveContact: onArchiveContact,
                                onDeleteContact: onDeleteContact,
                                isUpdateInProgress: ALLOW_EDIT_CONTACT && !!contact.userId() && isPending(contact.userId())
                            }, contact.id())) : /*#__PURE__*/ _jsx(PageEmptyState, {
                            illustration: /*#__PURE__*/ _jsx(EmptyStateIllustration, {}),
                            title: emptyStateTexts.noMatchTitle(),
                            message: emptyStateTexts.noMatchMessage(),
                            actions: /*#__PURE__*/ _jsx(Button, {
                                testId: "contacts-dashboard-empty-search-clear-btn",
                                onClick: onClearFilters,
                                children: emptyStateTexts.clearFilters()
                            })
                        })
                    ]
                }) : /*#__PURE__*/ _jsx(PageEmptyState, {
                    testId: "empty-state-page",
                    illustration: /*#__PURE__*/ _jsx(EmptyStateIllustration, {}),
                    message: emptyStateTexts.noContactsMessage(),
                    actions: /*#__PURE__*/ _jsx(CreateProposalButton, {
                        testId: "contacts-empty-state-new-btn",
                        text: emptyStateTexts.createAgreement(),
                        hidePrefix: true
                    })
                })
            }))
        ]
    });
}
export function ContactsDashboard() {
    const { contacts, isLoading, isFetching, refresh, optimisticUpdate, error } = useContacts({
        refreshOnMount: true
    });
    const { t: contactsTexts } = useLocaleKeys('pages.contactsDashboard');
    useSetDocumentTitle(contactsTexts.title());
    const handleRefresh = async ()=>{
        const result = await refresh();
        return result.data;
    };
    if (isLoading) {
        return /*#__PURE__*/ _jsx(PageLoadingState, {
            header: contactsTexts.title()
        });
    }
    if (error) {
        return /*#__PURE__*/ _jsx(PageErrorState, {
            testId: "error-state-page",
            title: contactsTexts.title(),
            onRefresh: handleRefresh
        });
    }
    return /*#__PURE__*/ _jsx(ActualContactsDashboard, {
        data: contacts,
        isRefreshing: isFetching,
        refresh: handleRefresh,
        optimisticUpdate: optimisticUpdate
    });
}
